"use client"

import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import type { CartItemData, ShippingMethod } from "@/components/cart/types"
import type { SellerDetail } from "@/components/cart/paypal-checkout"
import type { ShippingFormData } from "@/components/cart/shipping-form"
import { useCartCalculations } from "@/hooks/use-cart-calculations"
import { usePreCheckoutValidation, type ValidationError, type SellerAvailabilityCheck } from "@/hooks/use-pre-checkout-validation"
import { buildSellerDetails } from "@/lib/checkout-seller-details"
import {
  buildCheckoutPayload,
  type CheckoutPayload,
  type CheckoutPayloadExternalShipping,
} from "@/lib/checkout-payload"
import { isCountryBannedForShipping as isPayPalCountryBanned } from "@/lib/paypal-countries"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import { fetchCartLines } from "@/lib/cart/fetch-cart-lines"
import { reserveCart } from "@/lib/cart/cart-reserve"
import { parseListingImageRefs } from "@/lib/listings/parse-listing-images"
import { asRawImageRef } from "@/lib/storage/public-url"

interface UseCheckoutSessionOptions {
  scopeListingId: string
  userId: string
  enabled: boolean
}

interface CheckoutSessionData {
  items: CartItemData[]
  currencyCode: string
  subtotal: number
  shippingTotal: number
  serviceFee: number
  total: number
  allShippingMethodsSelected: boolean
  hasUnavailableItems: boolean
  sellerDetails: SellerDetail[]
  isLoading: boolean
  isValidating: boolean
  validationError: ValidationError | null
  clearValidationError: () => void
  buildPayload: (
    formData: ShippingFormData,
    externalShipping?: CheckoutPayloadExternalShipping | null,
  ) => CheckoutPayload
  isCountryBannedForShipping: (countryCode: string) => boolean
  updateShippingMethod: (itemId: string, methodId: string) => void
}

/**
 * Map raw /api/cart rows to CartItemData[].
 * Equivalent to the preloadedAsCartItems logic that was in ChatCheckoutDialog.
 */
function mapRowsToCartItems(rows: any[], listingId: string): CartItemData[] {
  return rows
    .filter((r) => String(r?.listing_id ?? r?.listings?.id ?? "") === listingId)
    .map((r: any) => {
      const rawListings = r.listings
      const L = Array.isArray(rawListings) ? rawListings[0] ?? {} : rawListings ?? {}
      // Canonical funnel instead of a local JSON.parse fork (the fork class
      // that caused the 2026-07-27 alias sweep). RAW exit: `images[0]` is
      // snapshotted into `order_items.image_url` by `lib/checkout-payload.ts`,
      // so resolving here would write absolute URLs back to the DB (§9).
      const parsedImages = parseListingImageRefs(L.image_urls)
      const shippingMethods: ShippingMethod[] = Array.isArray(L.shippingMethods) ? L.shippingMethods : []
      return {
        id: r.id,
        listingId: L.id ?? r.listing_id,
        sellerId: L.user_id ?? "",
        sellerName: L.sellerUsername ?? "Unknown",
        sellerAvatarUrl: asRawImageRef(L.sellerAvatarUrl as string | undefined),
        sellerCheckoutNote: L.sellerCheckoutNote ?? null,
        name: L.title ?? "Unknown Item",
        description: L.description ?? "",
        price: Number(L.price) ?? 0,
        currency: L.currency ?? DEFAULT_CURRENCY,
        quantity: Math.max(1, r.quantity ?? 1),
        maxQuantity: Math.max(1, L.quantity ?? 1),
        images: parsedImages,
        condition: L.condition ?? "used",
        grade: L.condition?.toLowerCase() === "new" ? undefined : L.grade,
        shippingMethods,
        selectedShippingMethod: L.selectedShippingMethod ?? (shippingMethods.length === 1 ? shippingMethods[0] : null),
        updated_at: r.updated_at ?? new Date().toISOString(),
        paypalMerchantId: r.paypalMerchantId ?? null,
        paypalCapabilities: r.paypalCapabilities ?? [],
        // Rail-availability is the only legitimate gating signal (never
        // paypalMerchantId). The /api/cart row already carries it; carry it
        // through so the pre-checkout gate and hasUnavailableItems are correct
        // on the Buy Now / chat path, not just the cart.
        availableProviders: r.availableProviders ?? [],
        // WS4.5 — Shippo discriminator + parcel dims (mirrors cart-provider
        // mapping added in WS4.4). `null` for legacy listings (the route
        // serialises `?? null`); `deriveShippingMode` treats null/undefined
        // as `'legacy'` so Buy-Now / chat flows preserve the byte-for-byte
        // legacy path until a live_rate listing flows through.
        shippingMode: (L.shipping_mode as "legacy" | "live_rate" | null) ?? null,
        parcelWeight: L.parcel_weight ?? null,
        parcelLength: L.parcel_length ?? null,
        parcelWidth: L.parcel_width ?? null,
        parcelHeight: L.parcel_height ?? null,
        // Slice C2 (2026-06-23): cart contract pins these to 'g' / 'mm' or null.
        parcelMassUnit: L.parcel_mass_unit ?? null,
        parcelDistanceUnit: L.parcel_distance_unit ?? null,
      } as CartItemData
    })
}

/**
 * Unified hook for Chat/Buy Now checkout data preparation.
 * Always fetches from /api/cart (server-enriched data), never relies on CartProvider for checkout data.
 * Fixes P1 bug where scopedItems from CartProvider could override server data with un-enriched items.
 */
export function useCheckoutSession({ scopeListingId, userId, enabled }: UseCheckoutSessionOptions): CheckoutSessionData {
  const [rawRows, setRawRows] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [shippingOverrides, setShippingOverrides] = useState<Record<string, string>>({})
  const { isValidating, validationError, validate, clearError } = usePreCheckoutValidation()
  const validationRanRef = useRef(false)

  // Fetch cart data and reserve items when dialog opens
  useEffect(() => {
    if (!enabled) {
      validationRanRef.current = false
      return
    }
    setIsLoading(true)
    ;(async () => {
      try {
        // Helper throws on non-2xx / parse failure; preserve prior "empty array
        // on error" semantics with a single catch — keeps the dialog usable
        // even when the cart endpoint blips.
        const rows = await fetchCartLines({ userId }).catch(() => [])
        setRawRows(rows)

        // Reserve items
        const itemsToReserve = rows
          .filter((r) => String(r?.listing_id ?? r?.listings?.id ?? "") === scopeListingId)
          .map((r) => ({ listingId: scopeListingId, quantity: Number(r?.quantity ?? 1) || 1 }))
        if (itemsToReserve.length > 0) {
          // Fire-and-forget: the dialog opens regardless of reserve outcome;
          // OOS / availability is checked downstream in the cart flow. Result
          // is intentionally ignored — preserves the pre-extract semantics
          // where the inline `await fetch(...)` also did not branch on the
          // response. Builder never throws, so no try/catch is needed.
          await reserveCart({ items: itemsToReserve })
        }

        // Rail-aware pre-checkout gate. Source `availableProviders` from the
        // server-enriched row — NOT paypalMerchantId. The narrow
        // SellerAvailabilityCheck type makes this field non-optional, so
        // dropping it (the old `as any` bug that false-blocked Buy Now) is now
        // a compile error.
        if (!validationRanRef.current) {
          validationRanRef.current = true
          const itemsForValidation: SellerAvailabilityCheck[] = rows
            .filter((r) => String(r?.listing_id ?? r?.listings?.id ?? "") === scopeListingId)
            .map((r: any) => ({
              sellerId: r.listings?.user_id ?? "",
              availableProviders: r.availableProviders ?? [],
              name: r.listings?.title ?? "Unknown Item",
            }))
          if (itemsForValidation.length > 0) {
            await validate(itemsForValidation)
          }
        }
      } finally {
        setIsLoading(false)
      }
    })()
  }, [scopeListingId, enabled, userId, validate])

  // Map raw rows to CartItemData, apply shipping overrides
  const items = useMemo(() => {
    const baseItems = mapRowsToCartItems(rawRows, scopeListingId)
    if (Object.keys(shippingOverrides).length === 0) return baseItems
    return baseItems.map((item) => {
      const overrideMethodId = shippingOverrides[item.id]
      if (!overrideMethodId) return item
      const method = item.shippingMethods.find((m) => m.id === overrideMethodId)
      return method ? { ...item, selectedShippingMethod: method } : item
    })
  }, [rawRows, scopeListingId, shippingOverrides])

  const currencyCode = useMemo(() => {
    const codes = Array.from(
      new Set(
        items
          .map((i) => i.currency || DEFAULT_CURRENCY)
          .filter((c): c is string => typeof c === "string" && c.trim().length > 0)
          .map((c) => c.toUpperCase()),
      ),
    )
    return codes.length === 1 ? codes[0] : DEFAULT_CURRENCY
  }, [items])

  const calcs = useCartCalculations(items)
  const sellerDetails = useMemo(() => buildSellerDetails(items), [items])

  const isCountryBanned = useCallback(
    (countryCode: string): boolean => {
      if (!countryCode) return false
      return items.some((item) => {
        if (!item.selectedShippingMethod) {
          return (
            item.shippingMethods.length > 0 &&
            item.shippingMethods.every((method) => isPayPalCountryBanned(countryCode, method.bannedCountries))
          )
        }
        return isPayPalCountryBanned(countryCode, item.selectedShippingMethod.bannedCountries)
      })
    },
    [items],
  )

  const buildPayload = useCallback(
    (
      formData: ShippingFormData,
      externalShipping?: CheckoutPayloadExternalShipping | null,
    ): CheckoutPayload => {
      return buildCheckoutPayload(
        userId,
        items,
        formData,
        currencyCode,
        {
          subtotal: calcs.subtotal,
          shippingTotal: calcs.shippingTotal,
          serviceFee: calcs.serviceFee,
          total: calcs.total,
        },
        undefined,
        externalShipping,
      )
    },
    [userId, items, currencyCode, calcs.subtotal, calcs.shippingTotal, calcs.serviceFee, calcs.total],
  )

  const updateShippingMethod = useCallback((itemId: string, methodId: string) => {
    setShippingOverrides((prev) => ({ ...prev, [itemId]: methodId }))
  }, [])

  return {
    items,
    currencyCode,
    subtotal: calcs.subtotal,
    shippingTotal: calcs.shippingTotal,
    serviceFee: calcs.serviceFee,
    total: calcs.total,
    allShippingMethodsSelected: calcs.allShippingMethodsSelected,
    hasUnavailableItems: calcs.hasUnavailableItems,
    sellerDetails,
    isLoading,
    isValidating,
    validationError,
    clearValidationError: clearError,
    buildPayload,
    isCountryBannedForShipping: isCountryBanned,
    updateShippingMethod,
  }
}
